// Tableau de bord — porté de apps/console/design/Dashboard V2.dc.html (structure + langage visuel).
// ⚠️ ZÉRO MOCK (§23.0) : la BANDE chiffres-clés est alimentée par des compteurs RÉELS (annuaire) ; les
// sections non branchées (santé services, locations, alertes NF525, journal) affichent un état HONNÊTE
// « à venir » — jamais les données de démo du design (jauges, alertes, journal fictifs).
import { dateLongNoYear, dateShort as dfDateShort, dateWithWeekday, time as dfTime } from '@orgazone/date-format';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { buildTriageSources, rentalBuckets, type TriageItem } from '../triage';
import { api, type ActivityEvent, type DashboardData, type DeviceRow, type HealthMeasure, type LegalWatchView, type ServiceHealth } from '../api';
import { DeviceName, deviceTitle } from '../device-name';
import { familyLabel } from '../event-families';
import { latencyLegend } from '../service-health';
import { mutedDevices } from '../sync-health';
import { NATURE_LABEL } from '../equipment-types';
import { EmptyState } from '@orgazone/ui-web';

// ── « Journal d'activité » (maquette : timeline point+fil, pastille de famille, méta mono, filtres par
// famille, pagination 6/page). Flux 100 % RÉEL : agrégation serveur des journaux de l'annuaire — le
// journal d'AUTH (IdP) vit dans l'écran Utilisateurs, pas dupliqué ici. ──────────────────────────────
const FAM_COLOR: Record<ActivityEvent['family'], { bg: string; fg: string; dot: string }> = {
  provisioning: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', dot: 'var(--chart-ok)' },
  flotte: { bg: 'var(--in-bg)', fg: 'var(--in-fg)', dot: 'var(--chart-in)' },
  blocages: { bg: 'var(--da-bg)', fg: 'var(--da-fg)', dot: 'var(--chart-da)' },
  'données': { bg: 'var(--vi-bg)', fg: 'var(--vi-fg)', dot: 'var(--chart-vi)' },
  clients: { bg: 'var(--wa-bg)', fg: 'var(--wa-fg)', dot: 'var(--chart-wa)' },
  plateforme: { bg: 'var(--ne-bg)', fg: 'var(--ne-fg)', dot: 'var(--chart-ne)' },
};
const ACT_PER = 6;
function ActivityFeed({ events, error }: { events: ActivityEvent[] | null; error: string | null }) {
  const [fam, setFam] = useState<'tout' | ActivityEvent['family']>('tout');
  const [page, setPage] = useState(0);
  // ⚠️ L'échec passe AVANT l'attente : sans ça, une source HS laissait « Chargement… » à l'écran
  // pour toujours — une panne déguisée en patience (§23.0).
  if (error) return <div className="err">Journal indisponible : {error}</div>;
  if (!events) return <div className="muted">Chargement du journal…</div>;
  if (events.length === 0) return <EmptyState compact icon="history" title="Aucun événement" note="Chaque action de la Console (mise en service, flotte, blocages, données…) s'inscrira ici au fil de l'activité." />;
  const fams: ('tout' | ActivityEvent['family'])[] = ['tout', ...[...new Set(events.map((e) => e.family))]];
  const rows = events.filter((e) => fam === 'tout' || e.family === fam);
  const maxPage = Math.max(0, Math.ceil(rows.length / ACT_PER) - 1);
  const p = Math.min(page, maxPage);
  const shown = rows.slice(p * ACT_PER, p * ACT_PER + ACT_PER);
  // Aujourd'hui → l'heure suffit ; sinon la date courte. Rendu partagé (@orgazone/date-format).
  const fmtAt = (s: string) => { const d = new Date(s); return new Date().toDateString() === d.toDateString() ? dfTime(d) : dfDateShort(d); };
  const navBtn = (dir: -1 | 1, disabled: boolean) => (
    <button onClick={() => setPage(p + dir)} disabled={disabled} className="pg-btn">
      {dir < 0 ? '←' : '→'}
    </button>
  );
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {fams.map((f) => (
          <button key={f} onClick={() => { setFam(f); setPage(0); }}
            style={{ height: 24, padding: '0 9px', borderRadius: 'var(--r-xs)', border: `1px solid ${fam === f ? 'var(--in-fg)' : 'var(--ink200)'}`, background: fam === f ? 'var(--in-bg)' : 'transparent', color: fam === f ? 'var(--in-fg)' : 'var(--ink500)', fontSize: 'var(--t-sub)', fontWeight: 600, cursor: 'pointer' }}>
            {f === 'tout' ? f : familyLabel(f)}
          </button>
        ))}
      </div>
      {shown.map((e, i) => (
        <div key={`${e.at}-${i}`} style={{ display: 'grid', gridTemplateColumns: '38px 10px minmax(0,1fr)', gap: 9, padding: '7px 0' }}>
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', paddingTop: 2 }}>{fmtAt(e.at)}</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span className="dot" style={{ background: FAM_COLOR[e.family].dot }} />
            {i < shown.length - 1 && <span style={{ width: 1, flex: 1, background: 'var(--ink200)', marginTop: 3 }} />}
          </div>
          <div style={{ minWidth: 0, paddingBottom: 3 }}>
            <div style={{ fontSize: 'var(--t-dense)', lineHeight: 1.4, color: 'var(--ink700)' }}>{e.text}</div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 6px', marginTop: 3 }}>
              <span style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--r-xs)', background: FAM_COLOR[e.family].bg, color: FAM_COLOR[e.family].fg }}>{familyLabel(e.family)}</span>
              {e.meta && <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', flex: '1 1 100%', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.45 }}>{e.meta}</span>}
            </div>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingTop: 9, borderTop: '1px solid var(--ink100)' }}>
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
          {rows.length ? `${p * ACT_PER + 1}–${Math.min((p + 1) * ACT_PER, rows.length)} sur ${rows.length}` : 'aucun évènement'}
        </span>
        <div style={{ flex: 1 }} />
        {navBtn(-1, p === 0)}{navBtn(1, p >= maxPage)}
      </div>
    </>
  );
}

// ── « À traiter » (maquette `triage`) : items NUMÉROTÉS filtrés n > 0, max 5 + « + N autres », pastille
// de compte colorée par gravité, cliquables vers l'écran cible. 7 SOURCES RÉELLES (annuaire, migrations,
// staff, brouillons, blocages, parc) — une source indisponible est DITE en pied (jamais un item omis en
// silence, §23.0). Les items BO-dépendants (périodes fiscales, caisses hors-ligne) viendront avec la sync. ──
/** Total des actions en attente. Extrait : la liste ET la pastille du titre doivent compter pareil. */
const totalTriage = (items: TriageItem[]) => items.filter((t) => t.n > 0).reduce((s, t) => s + t.n, 0);

/** Pastille de comptage du titre « À traiter » (gabarit `pill()` de la maquette : 23 px, plein rond). */
function TriageBadge({ n }: { n: number }) {
  // Le rouge au-delà de 6 : au-dessus, ce n'est plus une file d'attente, c'est un retard.
  const chaud = n > 6;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', minHeight: 23, padding: '2px 10px', borderRadius: 'var(--r-pill)',
      fontSize: 'var(--t-meta)', fontWeight: 600, letterSpacing: '.01em', whiteSpace: 'nowrap',
      background: chaud ? 'var(--da-bg)' : 'var(--wa-bg)', color: chaud ? 'var(--da-fg)' : 'var(--wa-fg)',
    }}>
      {n} action{n > 1 ? 's' : ''}
    </span>
  );
}

// ⚠️ Type + définitions des items : SOURCE UNIQUE `../triage` (partagée avec la cloche de topbar).
/**
 * L'ÉTAT DE LA VEILLE DES DÉFAILLANCES (SA-9.3), sous « À traiter » : quand elle est passée, combien de clients elle
 * surveille, et combien elle NE PEUT PAS surveiller faute de SIREN.
 * ⚠️ « Rien à traiter » ne vaut que pour les clients surveillés : sans cette ligne, un client sans SIREN passerait pour
 *    un client sans difficulté (décision utilisateur : le dire et le compter).
 */
export function LegalWatchLine({ view, error }: { view: LegalWatchView | null; error: string | null }) {
  if (error) return <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--wa-fg)', marginTop: 8 }}>⚠️ Veille des défaillances illisible : {error}</div>;
  if (!view) return null;
  const run = view.lastRun;
  const sansSiren = view.unwatched.length;
  return (
    <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 8, lineHeight: 1.5 }}>
      {run ? `Veille des défaillances passée le ${dfDateShort(new Date(run.at))} à ${dfTime(new Date(run.at))} · ${run.watched} client${run.watched > 1 ? 's' : ''} surveillé${run.watched > 1 ? 's' : ''}` : 'Veille des défaillances : pas encore passée'}
      {sansSiren > 0 && (
        <span title={view.unwatched.map((u) => u.name).join(', ')} style={{ color: 'var(--wa-fg)' }}>
          {' · '}{sansSiren} client{sansSiren > 1 ? 's' : ''} non surveillé{sansSiren > 1 ? 's' : ''} : SIREN manquant
        </span>
      )}
      {run && run.errors.length > 0 && (
        <span title={run.errors.map((e) => `${e.siren} (${e.source}) : ${e.error}`).join('\n')} style={{ color: 'var(--wa-fg)' }}>
          {' · '}{run.errors.length} interrogation{run.errors.length > 1 ? 's' : ''} en échec
        </span>
      )}
    </div>
  );
}

function TriageList({ items, failedSources }: { items: TriageItem[]; failedSources: number }) {
  const shown = items.filter((t) => t.n > 0);
  const top = shown.slice(0, 5);
  return (
    <>
      {shown.length === 0
        ? <EmptyState compact icon="check" title="Rien à traiter" note="Mises en service, retards de location, migrations, invitations, blocages : tout est au vert." />
        : <>
          {top.map((t, i) => (
            <Link key={t.label} to={t.to} className="triage-row" style={{ alignItems: 'flex-start', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
              <span className="triage-num" style={{ background: `var(--${t.kind}-bg)`, color: `var(--${t.kind}-fg)` }}>0{i + 1}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 2, lineHeight: 1.45 }}>{t.detail}</div>
              </div>
              <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: `var(--${t.kind}-fg)`, flexShrink: 0 }}>{t.n}</span>
            </Link>
          ))}
          {shown.length > 5 && <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 8 }}>+ {shown.length - 5} autre{shown.length - 5 > 1 ? 's' : ''} type{shown.length - 5 > 1 ? 's' : ''} d'action</div>}
        </>}
      {failedSources > 0 && (
        <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--wa-fg)', marginTop: 8 }}>
          ⚠️ {failedSources} source{failedSources > 1 ? 's' : ''} de triage indisponible{failedSources > 1 ? 's' : ''} — la liste peut être incomplète.
        </div>
      )}
    </>
  );
}

// ── « Locations courtes » (maquette `rentDash`) : mini-échéancier par appareil — barre de progression
// positionnée dans une fenêtre J−3 → J+10 (span 13), couleur PAR FAMILLE (Mobile Partner bleu / Sliderz violet),
// « N j » + dates aux extrémités, légende + total. Données RÉELLES (`device`, régime courte). ──────────
const R_SPAN = 13, R_OFF = 3, R_DAY = 86400000;
const rFmt = dfDateShort; // « 04/08 » — tiret si absent
function ShortRentals({ devices, error }: { devices: DeviceRow[] | null; error: string | null }) {
  if (error) return <div className="err">Parc indisponible : {error}</div>;
  if (!devices) return <div className="muted">Chargement du parc…</div>;
  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
  const di = (s: string) => Math.round((new Date(s).setHours(0, 0, 0, 0) - t0.getTime()) / R_DAY);
  const rows = devices
    .filter((d) => d.status === 'assigned' && d.rentalType === 'courte' && d.rentalStart)
    .sort((a, b) => (a.rentalEnd ?? '9999') < (b.rentalEnd ?? '9999') ? -1 : 1)
    .slice(0, 6);
  if (rows.length === 0) return <EmptyState compact icon="calendar" title="Aucune location courte en cours" note="Les locations courtes du parc s'afficheront ici avec leurs dates et leur progression — « Nouvelle location » depuis l'écran Locations." />;
  return (
    <>
      {rows.map((d) => {
        const a = di(d.rentalStart!), b = d.rentalEnd ? di(d.rentalEnd) : R_SPAN - R_OFF;
        const from = Math.max(0, a + R_OFF), to = Math.min(R_SPAN, b + R_OFF);
        const kind = d.nature === 'mobile-partner' ? 'in' : 'vi';
        return (
          <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '92px minmax(0,1fr)', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--ink100)', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deviceTitle(d)}><DeviceName d={d} /></div>
              <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 1 }}>{d.nature ? NATURE_LABEL[d.nature] : 'sans type'}</div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.tenantName ?? '—'} · {d.storeName ?? '—'}</span>
                <div style={{ flex: 1 }} />
                <span className="mono" style={{ fontSize: 'var(--t-meta)', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, color: `var(--${kind}-fg)` }}>{d.rentalEnd ? `${b - a + 1} j` : 'sans fin'}</span>
              </div>
              <div style={{ position: 'relative', height: 6, borderRadius: 'var(--r-r3)', background: 'var(--ink100)', marginTop: 6, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${(from / R_SPAN) * 100}%`, width: `${Math.max(4, ((to - from) / R_SPAN) * 100)}%`, borderRadius: 'var(--r-r3)', background: `var(--chart-${kind})` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', color: 'var(--ink400)' }}>{rFmt(d.rentalStart)}</span>
                <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', color: 'var(--ink400)' }}>{rFmt(d.rentalEnd)}</span>
              </div>
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 14, marginTop: 11, alignItems: 'center' }}>
        {([['Mobile Partner', 'var(--in-bg)', 'var(--chart-in)'], ['Sliderz', 'var(--vi-bg)', 'var(--chart-vi)']] as const).map(([l, bg, bd]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 8, borderRadius: 'var(--r-r3)', background: bg, border: `1px solid ${bd}` }} />
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink600)' }}>{l}</span>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', whiteSpace: 'nowrap' }}>
          {rows.length} location{rows.length > 1 ? 's' : ''} courte{rows.length > 1 ? 's' : ''} en cours ou à venir
        </span>
      </div>
    </>
  );
}

/**
 * Couleur/label par statut de service (sonde réelle). Le SERVEUR range la sonde selon ses paliers (`HealthMeasure`).
 *
 * ⚠️ `error` ÉTAIT ORANGE ICI ET ROUGE À LA PAGE DE SANTÉ — le même fait, deux gravités selon l'écran
 * regardé. Constaté en bêta le 2026-08-18 sur le store privé arrêté. Un service qui répond hors 2xx
 * ne sert pas : c'est une panne, et une panne se peint pareil partout.
 */
const SVC: Record<ServiceHealth['status'], { fg: string; bg: string; label: string }> = {
  ok:      { fg: 'var(--chart-ok)', bg: 'var(--ok-bg)', label: '' },
  warn:    { fg: 'var(--chart-wa)', bg: 'var(--wa-bg)', label: '' },
  high:    { fg: 'var(--chart-da)', bg: 'var(--da-bg)', label: 'latence élevée' },
  error:   { fg: 'var(--chart-da)', bg: 'var(--da-bg)', label: 'répond mais pas sain' },
  down:    { fg: 'var(--chart-da)', bg: 'var(--da-bg)', label: 'à l’arrêt' },
  pending: { fg: 'var(--chart-ne)', bg: 'var(--ne-bg)', label: 'mesure en cours' },
};

// Jauge d'un service : latence réelle + barre + échelle (repères 100/300/500) + p95 + uptime.
// ⚠️ La tuile ENTIÈRE mène à /sante (maquette) : voir une latence rouge sans pouvoir l'ouvrir d'un clic
// oblige à repartir chercher le lien de la section — le geste évident doit être le bon.
function ServiceGauge({ s }: { s: ServiceHealth }) {
  const c = SVC[s.status];
  const pct = s.lastMs != null ? Math.min(100, (s.lastMs / 700) * 100) : 0;
  const alert = s.status === 'down' || s.status === 'high' || s.status === 'error';
  return (
    <Link to="/sante" className={alert ? 'svc alert' : 'svc'} title={`Santé détaillée · ${s.name}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="dot" style={{ width: 9, height: 9, background: c.fg, boxShadow: `0 0 0 4px ${c.bg}` }} />
        <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>{s.name}</span>
        <div style={{ flex: 1 }} />
        {/* ⚠️ « % ok » est une part de RELEVÉS, pas une disponibilité : sans sa fenêtre, « 100 % ok »
            se lit comme une promesse alors qu'il ne couvre que quelques minutes — un service tombé il
            y a une heure et revenu l'afficherait aussi. */}
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }} title={s.uptime != null ? `${s.samples} relevés sur ${s.windowMinutes} min` : undefined}>
          {s.uptime != null ? `${s.uptime}% ok · ${s.windowMinutes} min` : ''}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10 }}>
        {s.lastMs != null
          ? <><span className="mono" style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1, color: c.fg }}>{s.lastMs}</span><span className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>ms</span></>
          : <span style={{ fontSize: 'var(--t-control)', fontWeight: 600, color: c.fg }}>{c.label || '—'}</span>}
      </div>
      {s.lastMs != null && (
        <>
          {/* ⚠️ La barre montre l'INSTANT, la couleur suit la MÉDIANE : sans repère, une barre courte
              et orange était incompréhensible (« 14 ms » en vert vif sur fond orange, signalé le
              2026-08-11). Le trait marque le p95 — on voit d'un coup que l'instant est bon ET que la
              traîne existe, au lieu d'avoir à choisir laquelle des deux vérités croire. */}
          <div style={{ position: 'relative', height: 7, borderRadius: 'var(--r-r4)', background: 'var(--ink100)', marginTop: 11 }}>
            <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${pct}%`, borderRadius: 'var(--r-r4)', background: `linear-gradient(90deg,var(--chart-ok),${c.fg})` }} />
            {s.p95 != null && s.p95 !== s.lastMs && (
              <span
                title={`p95 ${s.p95} ms — médiane ${s.median ?? '—'} ms (c’est elle qui décide la couleur)`}
                style={{
                  position: 'absolute', top: -2, height: 11, width: 2, borderRadius: 1,
                  left: `min(calc(100% - 2px), ${Math.min(100, (s.p95 / 700) * 100)}%)`,
                  background: 'var(--ink600)',
                }}
              />
            )}
          </div>
          <div style={{ position: 'relative', height: 12, marginTop: 4 }}>
            <span className="mono" style={{ position: 'absolute', left: 0, fontSize: 9, color: 'var(--ink400)' }}>0</span>
            <span className="mono" style={{ position: 'absolute', left: '14.3%', transform: 'translateX(-50%)', fontSize: 9, color: 'var(--ink400)' }}>100</span>
            <span className="mono" style={{ position: 'absolute', left: '42.9%', transform: 'translateX(-50%)', fontSize: 9, color: 'var(--ink400)' }}>300</span>
            <span className="mono" style={{ position: 'absolute', left: '71.4%', transform: 'translateX(-50%)', fontSize: 9, color: 'var(--ink400)' }}>500</span>
            <span className="mono" style={{ position: 'absolute', right: 0, fontSize: 9, color: 'var(--ink400)' }}>700+</span>
          </div>
        </>
      )}
      <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.url}</div>
      {alert && (
        <div style={{ fontSize: 'var(--t-sub)', color: c.fg, marginTop: 8 }}>
          {c.label}
          {s.status === 'error' && s.lastCode != null ? ` · HTTP ${s.lastCode}` : ''}
          {/* La MÉDIANE justifie la couleur ; l'afficher évite la question « pourquoi rouge alors que
              le dernier relevé est bon ? » — c'est elle qu'on a regardée, elle qu'on montre. */}
          {s.status === 'high' && s.median != null ? ` · médiane ${s.median} ms` : ''}
        </div>
      )}
      {s.status === 'pending' && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', marginTop: 8 }}>collecte en cours ({s.samples}/12 relevés)</div>}
    </Link>
  );
}

/** Ligne de la bande de chiffres-clés. `note` remplace le nombre quand celui-ci ne MESURE rien (§23.0). */
// ⚠️ `n` accepte une CHAÎNE : une mesure indisponible s'affiche « — », jamais 0. Un zéro affirme
// « il n'y en a aucun » ; le tiret dit « je ne sais pas » — et ce n'est pas la même information
// pour qui doit agir (§23.0).
function BandRow({ color, label, n, note }: { color: string; label: string; n: number | string; note?: string }) {
  return (
    <div className="band-row">
      <span className="dot" style={{ background: color }} />
      <span className="lab">{label}</span>
      {note
        ? <span className="lab" style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)', textAlign: 'right' }}>{note}</span>
        : <span className="n">{n}</span>}
    </div>
  );
}

// `aside` = contenu poussé à DROITE, juste avant le lien (maquette : la légende des paliers y vit).
// Sous l'en-tête, elle volait une ligne aux tuiles ; à droite, elle se lit au moment où on interprète
// les couleurs — c'est-à-dire en regardant les tuiles, pas avant.
// `wide` : l'en-tête de la SANTÉ, plus aéré (14 px dessous) et sa note en petit (10 px) — le dessin ne les fait pas toutes pareilles.
function Section({ title, badge, note, aside, cta, wide, children }: { title: string; badge?: ReactNode; note?: string; aside?: ReactNode; cta?: ReactNode; wide?: boolean; children: ReactNode }) {
  return (
    <div className="dcard dsec">
      {/* Un titre suivi de sa pastille de comptage s'en tient à 9 px (maquette, « À traiter ») : elle le qualifie. */}
      <div className={wide ? 'dsec-head wide' : 'dsec-head'} style={badge ? { gap: 9 } : undefined}>
        <h3>{title}</h3>
        {/* `badge` colle au TITRE (maquette), là où `aside` est repoussé à droite par l'espaceur : une
            pastille de comptage qualifie le titre, elle n'est pas une action de fin de ligne. */}
        {badge}
        {note && <span className={wide ? 'dsec-note micro' : 'dsec-note'}>{note}</span>}
        {(aside || cta) && <div style={{ flex: 1 }} />}
        {aside}
        {cta}
      </div>
      {children}
    </div>
  );
}

export function Dashboard() {
  const [d, setD] = useState<DashboardData | null>(null);
  const [health, setHealth] = useState<ServiceHealth[] | null>(null);
  const [measure, setMeasure] = useState<HealthMeasure | null>(null);
  // Kill-switch : compteurs RÉELS. ⚠️ `auto` vs `manuel` n'est pas une invention d'affichage — la
  // consigne porte sa `source` (`rule:…` posée par une règle, `manual` posée par un humain), et c'est
  // la distinction qui compte pour agir : une règle se lève en corrigeant sa cause, un blocage manuel
  // se lève en parlant à qui l'a posé. `devices` = équipements immobilisés (portée `device`).
  const [blocks, setBlocks] = useState<{ warn: number; lock: number; auto: number; manuel: number; devices: number } | null>(null);
  const [fleet, setFleet] = useState<DeviceRow[] | null>(null); // mini-échéancier « Locations courtes »
  const [activity, setActivity] = useState<ActivityEvent[] | null>(null); // journal d'activité RÉEL
  // Sources du TRIAGE « À traiter » — chaque échec est COMPTÉ (dit en pied, jamais omis en silence).
  const [migLate, setMigLate] = useState<number | null>(null);
  const [invited, setInvited] = useState<number | null>(null);
  const [draftsToReview, setDraftsToReview] = useState<number | null>(null);
  // La veille des défaillances : ses signalements (en tête de « À traiter ») et son état (sous la liste).
  const [legal, setLegal] = useState<LegalWatchView | null>(null);
  const [legalErr, setLegalErr] = useState<string | null>(null);
  const [muted, setMuted] = useState<ReturnType<typeof mutedDevices> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Panne PAR SECTION : une source HS doit se DIRE là où elle manque, pas laisser un spinner éternel.
  const [fleetErr, setFleetErr] = useState<string | null>(null);
  const [activityErr, setActivityErr] = useState<string | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);

  useEffect(() => { api.dashboard().then(setD).catch((e) => setErr((e as Error).message)); }, []);
  useEffect(() => {
    api.blocks().then((r) => setBlocks({
      warn: r.active.filter((b) => b.level === 'warn').length,
      lock: r.active.filter((b) => b.level === 'lock').length,
      auto: r.active.filter((b) => b.source.startsWith('rule:')).length,
      manuel: r.active.filter((b) => b.source === 'manual').length,
      devices: r.active.filter((b) => b.scope === 'device').length,
    }))
      .catch(() => setBlocks(null)); // indisponible → « — », jamais un zéro inventé (§23.0)
  }, []);
  useEffect(() => { api.devices().then((r) => { setFleet(r.devices); setFleetErr(null); }).catch((e) => { setFleet(null); setFleetErr((e as Error).message); }); }, []);
  useEffect(() => { api.activity().then((r) => { setActivity(r.events); setActivityErr(null); }).catch((e) => { setActivity(null); setActivityErr((e as Error).message); }); }, []);
  useEffect(() => { api.migrationsStatus().then((r) => setMigLate(r.stores.filter((x) => !x.upToDate).length)).catch(() => setMigLate(null)); }, []);
  useEffect(() => { api.staff().then((r) => setInvited(r.staff.filter((x) => x.status === 'invited').length)).catch(() => setInvited(null)); }, []);
  useEffect(() => { api.drafts().then((r) => setDraftsToReview(r.drafts.filter((x) => x.status === 'deposited').length)).catch(() => setDraftsToReview(null)); }, []);
  useEffect(() => { api.legalWatch().then((r) => { setLegal(r); setLegalErr(null); }).catch((e) => { setLegal(null); setLegalErr((e as Error).message); }); }, []);
  // Les appareils muets — LE calcul de la Supervision (`mutedDevices`), jamais un second jugement des seuils ici.
  useEffect(() => { api.supervision().then((r) => setMuted(mutedDevices(r.stores))).catch(() => setMuted(null)); }, []);
  // Santé des services : instantané + rafraîchissement toutes les 30 s (la sonde relève côté serveur).
  useEffect(() => {
    const load = () => api.healthServices()
      .then((r) => { setHealth(r.services); setMeasure(r.measure); setHealthErr(null); })
      .catch((e) => setHealthErr((e as Error).message));
    void load();
    const h = setInterval(load, 30_000);
    return () => clearInterval(h);
  }, []);

  if (err) return <div className="dash"><div className="err">Erreur : {err}</div></div>;
  if (!d) return <div className="dash"><div className="muted">Chargement…</div></div>;

  const now = new Date();
  const stamp = `relevé ${dfTime(now)} · ${dateWithWeekday(now)}`;
  const provisioning = d.stores.provisioning;
  // Fenêtre du mini-échéancier locations (maquette : J−3 → J+10, affichée en dates réelles).
  const rd = (n: number) => { const x = new Date(now); x.setDate(x.getDate() + n); return dateLongNoYear(x); };
  const rentRange = `${rd(-R_OFF)} → ${rd(R_SPAN - R_OFF)}`;

  // ── Triage « À traiter » : chaque item compte du RÉEL ; une source en échec (null) est comptée en pied.
  // ⚠️ Le découpage des échéances vient de `triage.ts` — il était RECOPIÉ ici, ce qui pouvait faire
  // diverger la carte du Dashboard et la cloche de la topbar sur la même flotte.
  const buckets = fleet === null ? null : rentalBuckets(fleet);
  const lateReturns = buckets?.late ?? null;
  const dueSoon = buckets?.dueSoon ?? null;
  // `locks` se DÉDUIT des blocages déjà chargés (un second état alimenté par le même appel divergeait).
  const triageSources = buildTriageSources({ legal: legal?.open ?? null, muted, lateReturns, locks: blocks?.lock ?? null, provisioning, dueSoon, draftsToReview, invited, migLate });
  const triageItems: TriageItem[] = triageSources.filter((s) => s.n !== null).map((s) => s.item(s.n!));
  const triageFailed = triageSources.filter((s) => s.n === null).length;

  return (
    <div className="dash">
      {/* ⚠️ TITRE + SOUS-TITRE (maquette) : le sur-titre « Console éditeur · bêta » répétait ce que le
          rail annonce déjà deux fois. Le relevé passe en sous-titre — il qualifie CE qu'on regarde
          (« ces chiffres datent de… »), ce qui est le rôle d'un sous-titre, pas d'une mention isolée
          à l'autre bout de la ligne.
          ⚠️ Les deux ACCÈS RAPIDES viennent de la maquette : depuis le tableau de bord, les deux
          gestes les plus fréquents sont créer un client/magasin et créer un utilisateur. Les atteindre
          demandait deux navigations. */}
      <div className="dash-head">
        <div>
          <h1>Tableau de bord</h1>
          <div className="stamp" style={{ marginTop: 3 }}>{stamp}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {/* ⚠️ LES RECETTES DE LA CHARTE SEULES (`.btn-cta`, `.btn-ghost`) : l'ancienne `.btn` de la Console y ajoutait
              son éclaircissement au survol et `.sm` resserrait la boîte — 36 px de haut forcés à la main, 13 px de marge au
              lieu des 16 du dessin. */}
          <Link to="/clients?nouveau=1" className="btn-cta">
            Créer un client ou un magasin
          </Link>
          <Link to="/staff?nouveau=1" className="btn-ghost">
            Créer un utilisateur
          </Link>
        </div>
      </div>

      {/* Bande chiffres-clés — RÉEL (annuaire) */}
      <div className="dcard band">
        <div className="band-cell">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="band-lab">parc client</span><div style={{ flex: 1 }} />
            <Link to="/clients" className="btn-link marque">Clients →</Link>
          </div>
          <div className="band-figures">
            <span className="band-figure"><span className="band-big">{d.tenants.total}</span><span className="band-unit">clients</span></span>
            {/* ⚠️ Pas de séparateur (maquette) : deux mesures juxtaposées, pas une division. Un « / »
                entre deux nombres se lit comme un ratio — « 6 clients pour 13 magasins » — alors qu'il
                s'agit de deux totaux indépendants. */}
            <span className="band-figure"><span className="band-big">{d.stores.total}</span><span className="band-unit">magasins</span></span>
          </div>
          <div className="band-list">
            <BandRow color="var(--chart-ne)" label="mono-magasin" n={d.tenants.mono} />
            <BandRow color="var(--chart-in)" label="multi-magasin" n={d.tenants.multi} />
            {/* ⚠️ L'agrégat (SA-2.9) n'est PAS branché : rien ne crée de base d'agrégat aujourd'hui, car
                elle se remplit par le feed change-log du back-office, qui n'existe pas encore. Afficher
                « 0 » ici ressemblerait à une MESURE alors que c'est une capacité absente — on nomme donc
                l'état au lieu de compter (§23.0). À rétablir en compteur le jour où le feed alimente. */}
            <BandRow color="var(--chart-vi)" label="bases Pilot" n={d.tenants.aggregates}
              note={d.tenants.multi > 0 && d.tenants.aggregates === 0 ? 'consolidation multi-magasin à brancher (Sliderz Pilot, SA-2.9)' : undefined} />
          </div>
          <div className="band-foot">{d.stores.depots} {d.stores.depots > 1 ? 'dépôts' : 'dépôt'} · {d.staff} {d.staff > 1 ? 'comptes' : 'compte'} staff</div>
        </div>

        <div className="band-cell">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="band-lab">blocages</span><div style={{ flex: 1 }} />
            <Link to="/blocages" className="btn-link marque">Blocages →</Link>
          </div>
          {/* Compteurs RÉELS du kill-switch (SA-4) ; indisponible → « — », jamais un zéro inventé (§23.0). */}
          <div className="band-figures">
            <span className="band-figure">
              <span className="band-big" style={{ color: blocks && blocks.lock > 0 ? 'var(--da-fg)' : undefined }}>{blocks ? blocks.warn + blocks.lock : '—'}</span>
              <span className="band-unit">{blocks ? 'en cours' : 'indisponible'}</span>
            </span>
          </div>
          {blocks && (
            <div className="band-list">
              {/* Libellés de la maquette : ce qui distingue les deux premières lignes est l'ORIGINE de
                  la consigne, pas sa sévérité — c'est elle qui dit à qui s'adresser pour la lever. */}
              <BandRow color="var(--chart-da)" label="automatiques (règles)" n={blocks.auto} />
              <BandRow color="var(--chart-wa)" label="manuels (super-admin)" n={blocks.manuel} />
              <BandRow color="var(--chart-ne)" label="équipements immobilisés" n={blocks.devices} />
            </div>
          )}
          <div className="band-foot">granularités : abonnement · magasin · caisse — appliqué à l'appairage</div>
        </div>

        <div className="band-cell">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="band-lab">parc matériel</span><div style={{ flex: 1 }} />
            <Link to="/appareils" className="btn-link marque">Appareils →</Link>
          </div>
          <div className="band-figures">
            <span className="band-figure">
              <span className="band-big" style={{ color: 'var(--ok-fg)' }}>{d.devices.available}</span>
              <span className="band-unit">disponibles en stock</span>
            </span>
          </div>
          <div className="band-list">
            <BandRow color="var(--chart-in)" label="en location courte" n={d.devices.rentalCourte} />
            <BandRow color="var(--chart-vi)" label="location longue durée" n={d.devices.rentalLongue} />
            <BandRow color="var(--chart-ne)" label="achetés par le client" n={d.devices.purchase} />
            {/* ⚠️ Compté depuis la flotte DÉJÀ chargée (échéancier des locations courtes) — même source
                que la tuile de tri, donc jamais deux chiffres différents pour la même chose. Tant que la
                flotte n'est pas lue, on ne prétend pas qu'il n'y en a aucun : « — » (§23.0). */}
            <BandRow color="var(--chart-da)" label="retours en retard" n={lateReturns ?? '—'} />
          </div>
          {/* ⚠️ LE PARC, C'EST CE QUE LA PAGE APPAREILS COMPTE « Parc total » : un appareil révoqué n'en fait plus partie. Le
              serveur ne les compte plus — deux chiffres pour le même parc se contrediraient d'un écran à l'autre. */}
          <div className="band-foot">{d.devices.total} {d.devices.total > 1 ? 'appareils' : 'appareil'} au parc · facturation dans Sliderz Business</div>
        </div>
      </div>

      {/* Santé des services — RÉEL (sonde de latence). Le détail vit sur l'écran dédié /sante (maquette). */}
      <Section
        wide
        title="Santé des services"
        // ⚠️ LA FENÊTRE ET LES PALIERS VIENNENT DU SERVEUR : la note disait « relevé toutes les minutes » quand la sonde
        //    relève toutes les 30 s, et la légende « 100–500 » quand les tuiles virent au rouge à 300.
        note={measure ? `médiane sur ${measure.samples} relevés · ${measure.minutes} min` : 'latence mesurée par la sonde'}
        aside={measure && <>
          {latencyLegend(measure).map((l) => (
            <span key={l.key} className="svc-legend"><span className="dot" style={{ background: l.dot }} /><span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>{l.label}</span></span>
          ))}
        </>}
        cta={<Link to="/sante" className="btn-link marque">Santé services →</Link>}
      >
        {healthErr && health === null ? (
          <div className="err">Sonde indisponible : {healthErr}</div>
        ) : health === null ? (
          <div className="muted">Chargement…</div>
        ) : health.length === 0 ? (
          <EmptyState compact icon="pulse" title="Aucun service surveillé" note={<>Ajoutez leurs sondes dans <Link to="/applications" className="btn-link marque">Applications</Link> — la Console mesurera latence et disponibilité chaque minute.</>} />
        ) : (
          <div className="svc-grid">{health.map((s) => <ServiceGauge key={s.id} s={s} />)}</div>
        )}
      </Section>

      <div className="dgrid">
        <Section title="Locations courtes" note={rentRange} cta={<Link to="/locations" className="btn-link marque">Locations →</Link>}>
          <ShortRentals devices={fleet} error={fleetErr} />
        </Section>

        {/* ⚠️ PAS de sous-titre ici (maquette) : le titre porte sa pastille de comptage, et rien d'autre.
            « trié par gravité — cliquer pour agir » décrivait le fonctionnement de la liste, ce que
            l'ordre et le curseur disent déjà. */}
        <Section title="À traiter" badge={totalTriage(triageItems) > 0 ? <TriageBadge n={totalTriage(triageItems)} /> : null}>
          <TriageList items={triageItems} failedSources={triageFailed} />
          <LegalWatchLine view={legal} error={legalErr} />
        </Section>
      </div>

      <div className="dgrid last">
        <Section title="Alertes NF525" note="supervision fiscale">
          <EmptyState compact icon="bell" title="Alertes NF525 — à venir" note="Périodes en attente, sync en retard, caisses hors-ligne, Z partielles : ces alertes viendront de la donnée de synchronisation réelle des caisses, pas avant." />
        </Section>
        {/* La tuile ne montre que la tête du journal : le lien vers l'écran complet (filtres, table
            triable, audit IdP) évite de conclure qu'il n'y a rien de plus à voir — maquette. */}
        <Section title="Journal d'activité" cta={<Link to="/journal" className="btn-link marque">Journal complet →</Link>}>
          <ActivityFeed events={activity} error={activityErr} />
        </Section>
      </div>
    </div>
  );
}
